/**
 * What the bell shows on top of the icon.
 */
export type AttentionBellKind = "none" | "dot" | "count";

export interface IAttentionBellState {
    kind: AttentionBellKind;

    /**
     * Only meaningful for "count".
     */
    count: number;

    /**
     * Only meaningful for "count". Red rather than purple.
     */
    isOverdue: boolean;
}

/**
 * The bell's badge.
 *
 * The number counts what the person has to do, never what they have not read,
 * and never the two added together — a bell reading "7" that turns out to be
 * three tasks and four newsletters teaches people to ignore it. Unread
 * notifications still deserve to be noticed, so they get a dot, which says
 * "there is something" without competing with a number that says "do this".
 *
 * Red is kept for the one case where it means something: a deadline has already
 * passed.
 *
 * @param count How many things are waiting on the person.
 * @param overdueCount How many of those are already late.
 * @param unreadCount How many unread notifications there are.
 * @param isApplicable False where Just nu is not this person's. The bell then
 * behaves exactly as it did before the feature existed.
 */
export function bellState(
    count: number,
    overdueCount: number,
    unreadCount: number,
    isApplicable: boolean,
): IAttentionBellState {
    if (!isApplicable) {
        // No Just nu for this person, so the old meaning stands: the number is
        // the unread one.
        return unreadCount > 0
            ? { kind: "count", count: unreadCount, isOverdue: false }
            : { kind: "none", count: 0, isOverdue: false };
    }

    if (count > 0) {
        return { kind: "count", count, isOverdue: overdueCount > 0 };
    }

    if (unreadCount > 0) {
        return { kind: "dot", count: 0, isOverdue: false };
    }

    return { kind: "none", count: 0, isOverdue: false };
}
